/**
 * Core: Canvas
 * -----------------------------------------------------------------------------
 * Initialises and configures Vue in Canvas.
 * - Gets global components.
 * - Gets dynamic components.
 * - Sets Vuex stores.
 * - Sets Vue methods.
 * - Sets Vue mixins.
 * - Initialises Vue.
 *
 */
import { createApp, defineAsyncComponent } from 'vue'

import * as Canvas from '~/core/canvas-functions'
import Methods from '~/core/canvas-methods'
import AppHandler from '~/core/app-handler'
import config from '~/../../canvas.config'

import VueSocialSharing from 'vue-social-sharing'

/**
 * DOM selectors.
 */
const selectors = {
  component: '[data-component-handle]',
  vueDesignModeElement: 'vue-design-mode-element',
  vueElement: 'vue-element',
}

/**
 * Create a new Canvas object.
 */
export default () => {

  /**
   * Set global variables.
   */
  const dynamicComponents = {}
  const vueDesignModeElement = document.getElementById(selectors.vueDesignModeElement)
  const vueElement = document.getElementById(selectors.vueElement)
  let app = {}

  const sectionEvents = [
    'shopify:section:load',
    'shopify:section:unload',
    'shopify:section:reorder',
  ]

  /**
   * Initialise Vue.
   */
  function init() {
    if (
      cnvs.settings.disableCanvas ||
      cnvs.page.type === 'gift_card' ||
      cnvs.page.type === 'password'
    ) {
      return
    }

    getApp()
    getDynamicComponents()
    Canvas.getGlobalComponents(app)
    Canvas.setVuePlugins(app)

    /**
     * Only include Bugsnag when API key was included in environment variables
     * on build, i.e. in Buddy.
     */
    if (cnvs.environment.bugsnagApiKey !== 'BUGSNAG_API_KEY') {
      app.use(Bugsnag.getPlugin('vue'))
    }

    /**
     * Loads the Vue Social Sharing plugin.
     */
    app.use(VueSocialSharing)

    /**
     * Mount to DOM.
     */
    app.mount(`#${selectors.vueElement}`)

    if (!Shopify.designMode) {
      return
    }

    /**
     * Remount to DOM on design mode section changes.
     */
    sectionEvents.forEach((event) => {
      document.addEventListener(event, restartVue)
    })
  }

  /**
   * Create and setup Canvas Vue app object.
   * @returns {Object}
   */
  function getApp() {
    app = createApp({
      name: 'Canvas',
      delimiters: ['${', '}'],
      components: dynamicComponents,
      data() {
        return {
          article: false,
          blog: false,
          collection: false,
          global: true,
          page: false,
          product: false,
          variant: false,
        }
      },
      mounted() {
        this.setDynamicComponentStates()
        this.setEventListeners()
      },
      unmounted() {
        this.removeEventListeners()
      },
      methods: Methods(dynamicComponents),
    })

    /**
     * Use canvas.config.js file to configure custom elements.
     * - Custom elements directly inside Vue instance must be defined here.
     * - Custom elements nested in components must be defined in the loader.
     */
    if (!config || !config.customElements.length) {
      return
    }

    app.config.compilerOptions.isCustomElement = (tag) => {
      return config.customElements.includes(tag)
    }
  }

  /**
   * Determines components to dynamically load in Vue.
   * - Based on elements with `data-component-handle` attribute.
   * - Creates component import for each.
   * - Must be run before Vue so can't access Vue methods.
   * - Create global object passed to Vue methods to update component states.
   */
  function getDynamicComponents() {
    const elements = [...document.querySelectorAll(selectors.component)]

    elements.forEach((element) => {
      const handle = element.dataset.componentHandle

      dynamicComponents[handle] = defineAsyncComponent({
        loader: () => import(
          /* webpackChunkName: 'component.[request]' */
          `~async/${handle}/${handle}.vue`
        ),
      })
    })
  }

  /**
   * Unmount and remount the Vue instance.
   */
  function restartVue() {

    /**
     * Remove section editor event listeners.
     */
    sectionEvents.forEach((event) => {
      document.removeEventListener(event, restartVue)
    })

    setTimeout(() => {

      /**
       * Remove active Vue instance and attributes.
       * - Replace HTML with up-to-date hidden content.
       */
      app.unmount(`#${selectors.vueElement}`)
      vueElement.removeAttribute('data-v-app')
      vueElement.innerHTML = vueDesignModeElement.innerHTML

      /**
       * Initialise Vue.
       */
      AppHandler().beforeVueInit()
      init()
      AppHandler().afterVueInit()
    }, 0)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
